import { ChildrenProp } from '../types_and_interfaces.ts'
import { useTranslation } from 'react-i18next'

export default function StackedTable({ children }: ChildrenProp) {
  return <div className="mt-10 px-4 sm:px-6 lg:px-8">{children}</div>
}

const TableHelmet = ({ children }: ChildrenProp) => {
  return <div className="flex justify-center sm:justify-end">{children}</div>
}
StackedTable.Helmet = TableHelmet

const Table = ({ children }: ChildrenProp) => {
  return (
    <div className="ring-1 ring-gray-300 sm:mx-0">
      <table className="min-w-full divide-y divide-gray-300">{children}</table>
    </div>
  )
}

StackedTable.Table = Table

const HeaderItem = ({ children }: ChildrenProp) => {
  return (
    <th className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">
      {children}
    </th>
  )
}

StackedTable.HeaderItem = HeaderItem

const TableHeader = ({ children }: ChildrenProp) => {
  return (
    <thead className="bg-gray-50">
      <tr>{children}</tr>
    </thead>
  )
}

StackedTable.Header = TableHeader

const TableBody = ({ children }: ChildrenProp) => {
  return <tbody>{children}</tbody>
}

StackedTable.Body = TableBody

const TableFooter = ({ children }: ChildrenProp) => {
  return (
    <tfoot>
      <tr>
        <td colSpan={100}>{children}</td>
      </tr>
    </tfoot>
  )
}

const RowItem = ({ children }: ChildrenProp) => {
  return (
    <td className={'hidden px-3 py-4 text-sm text-gray-700 sm:table-cell'}>
      {children}
    </td>
  )
}

StackedTable.RowItem = RowItem

StackedTable.Footer = TableFooter

export const LeftOffsetTopBorder = () => {
  return <div className="absolute -top-px left-6 right-0 h-px bg-gray-200" />
}

export const RightOffsetTopBorder = () => {
  return <div className="absolute -top-px left-0 right-6 h-px bg-gray-200" />
}

export const TableToolBar = ({ children }: ChildrenProp) => {
  return (
    <div className="flex flex-col items-start lg-sm:flex-row lg-sm:items-center">
      {children}
    </div>
  )
}

const plans = [
  {
    id: 1,
    name: 'Hobby',
    memory: '4 GB RAM',
    cpu: '4 CPUs',
    storage: '128 GB SSD disk',
    price: '$40',
    isCurrent: false,
  },
  {
    id: 2,
    name: 'Startup',
    memory: '8 GB RAM',
    cpu: '6 CPUs',
    storage: '256 GB SSD disk',
    price: '$80',
    isCurrent: true,
  },
  // More plans...
]

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

export function Example() {
  const { t: translate } = useTranslation()

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {translate('plans')}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {translate('your_team_is_on_the')}{' '}
            <strong className="font-semibold text-gray-900">
              {translate('startup')}
            </strong>{' '}
            {translate('the_next_payment')}
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            className="block rounded-md bg-primary-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
          >
            {translate('update_credit_card')}
          </button>
        </div>
      </div>
      <div className="-mx-4 mt-10 ring-1 ring-gray-300 sm:mx-0 sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead>
            <tr>
              <th
                scope="col"
                className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
              >
                {translate('plan')}
              </th>
              <th
                scope="col"
                className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell"
              >
                {translate('memory')}
              </th>
              <th
                scope="col"
                className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell"
              >
                {translate('cpu')}
              </th>
              <th
                scope="col"
                className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell"
              >
                {translate('storage')}
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
              >
                {translate('price')}
              </th>
              <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                <span className="sr-only">{translate('select')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan, planIdx) => (
              <tr key={plan.id}>
                <td
                  className={classNames(
                    planIdx === 0 ? '' : 'border-t border-transparent',
                    'relative py-4 pl-4 pr-3 text-sm sm:pl-6'
                  )}
                >
                  <div className="font-medium text-gray-900">
                    {plan.name}
                    {plan.isCurrent ? (
                      <span className="ml-1 text-primary-600">
                        ({translate('current_plan')})
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 flex flex-col text-gray-500 sm:block lg:hidden">
                    <span>
                      {plan.memory} / {plan.cpu}
                    </span>
                    <span className="hidden sm:inline">·</span>
                    <span>{plan.storage}</span>
                  </div>
                  {planIdx !== 0 ? (
                    <div className="absolute -top-px left-6 right-0 h-px bg-gray-200" />
                  ) : null}
                </td>
                <td
                  className={classNames(
                    planIdx === 0 ? '' : 'border-t border-gray-200',
                    'hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell'
                  )}
                >
                  {plan.memory}
                </td>
                <td
                  className={classNames(
                    planIdx === 0 ? '' : 'border-t border-gray-200',
                    'hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell'
                  )}
                >
                  {plan.cpu}
                </td>
                <td
                  className={classNames(
                    planIdx === 0 ? '' : 'border-t border-gray-200',
                    'hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell'
                  )}
                >
                  {plan.storage}
                </td>
                <td
                  className={classNames(
                    planIdx === 0 ? '' : 'border-t border-gray-200',
                    'px-3 py-3.5 text-sm text-gray-500'
                  )}
                >
                  <div className="sm:hidden">{plan.price}/mo</div>
                  <div className="hidden sm:block">
                    {plan.price}/{translate('month')}
                  </div>
                </td>
                <td
                  className={classNames(
                    planIdx === 0 ? '' : 'border-t border-transparent',
                    'relative py-3.5 pl-3 pr-4 text-right text-sm font-medium sm:pr-6'
                  )}
                >
                  <button
                    type="button"
                    disabled={plan.isCurrent}
                    className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
                  >
                    {translate('selectr')}
                    <span className="sr-only">, {plan.name}</span>
                  </button>
                  {planIdx !== 0 ? (
                    <div className="absolute -top-px left-0 right-6 h-px bg-gray-200" />
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
